import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, LinearProgress } from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import api from '../../services/api';
import useStyles from './styles';
import FiltrosHome, { FILTROS_INICIAIS } from './FiltrosHome';
import { intervalo } from './periodo';
import { MODULOS, LARGURAS, ROTULO_LARGURA, layoutPadrao, sanearLayout } from './modulos';
import { Kpi, Barras, Comparativo, Evolucao, ProximasReunioes, FollowUps, Auditoria, Importacoes, Atalhos } from './widgets';

const LISTAS = ['proximas-reunioes', 'followups', 'auditoria', 'importacoes'];

// Tema local da Home (o app não tem tema global): verde da marca nos botões primários.
const tema = createTheme({ palette: { primary: { main: '#4B8B33', dark: '#2F6420', contrastText: '#fff' } } });

function Modulo({ id, span, indice, total, editando, resumo, listas, erros, visao, acoes }) {
    const classes = useStyles();
    const m = MODULOS[id];

    let conteudo = null;
    if (m.dados && erros[m.dados]) {
        conteudo = <div className={classes.vazio}>Não foi possível carregar.</div>;
    } else if (m.tipo === 'kpi') {
        conteudo = resumo ? <Kpi dados={m.kpi(resumo.kpis)} emDestaque={m.destaque && !editando} /> : null;
    } else if (m.tipo === 'barras') {
        conteudo = resumo ? <Barras itens={m.barras(resumo)} /> : null;
    } else if (m.tipo === 'comparativo') {
        conteudo = resumo ? <Comparativo linhas={m.linhas(resumo)} /> : null;
    } else if (m.tipo === 'evolucao') {
        conteudo = resumo ? <Evolucao dados={resumo.evolucao} /> : null;
    } else if (m.tipo === 'atalhos') {
        conteudo = <Atalhos visao={visao} />;
    } else if (m.tipo === 'lista') {
        const linhas = listas[m.dados];
        if (linhas) {
            const Comp = { 'proximas-reunioes': ProximasReunioes, followups: FollowUps, auditoria: Auditoria, importacoes: Importacoes }[m.dados];
            conteudo = <Comp linhas={linhas} />;
        }
    }

    return (
        <section className={`${classes.card} ${classes['s' + span]} ${editando ? classes.cardEditando : m.destaque ? classes.cardDestaque : ''}`} aria-label={m.titulo}>
            <div className={classes.cardTopo}>
                <h2 className={classes.cardTitulo}>{m.titulo}</h2>
                {editando ? (
                    <div className={classes.ferramentas}>
                        <Button size="small" variant="outlined" className={classes.ferramenta} aria-label="Mover para trás"
                            disabled={indice === 0} onClick={() => acoes.mover(indice, -1)}>←</Button>
                        <Button size="small" variant="outlined" className={classes.ferramenta} aria-label="Mover para frente"
                            disabled={indice === total - 1} onClick={() => acoes.mover(indice, 1)}>→</Button>
                        <Button size="small" variant="outlined" className={classes.ferramenta} aria-label="Alterar largura"
                            onClick={() => acoes.largura(indice)}>{ROTULO_LARGURA[span]}</Button>
                        <Button size="small" variant="outlined" className={classes.ferramenta} aria-label="Remover módulo"
                            style={{ color: '#A63A1A' }} onClick={() => acoes.remover(indice)}>✕</Button>
                    </div>
                ) : (
                    m.link && <Link to={m.link.to} className={classes.cardLink}>{m.link.texto}</Link>
                )}
            </div>
            {conteudo}
        </section>
    );
}

const Home = () => {
    const classes = useStyles();
    const [nome] = useState(localStorage.getItem('name') || '');

    const [podeGestao, setPodeGestao] = useState(false);
    const [visao, setVisao] = useState(null);
    const [layout, setLayout] = useState([]);
    const layoutSalvo = useRef(null); // para "Cancelar" o modo de edição
    const [editando, setEditando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
    const [clientes, setClientes] = useState([]);
    const [assessores, setAssessores] = useState([]);

    const [resumo, setResumo] = useState(null);
    const [listas, setListas] = useState({});
    const [erros, setErros] = useState({});
    const [carregando, setCarregando] = useState(false);
    const requisicao = useRef(0);

    /* Visão e layout ------------------------------------------------------ */
    const carregarLayout = async (visaoDesejada) => {
        try {
            const { data } = await api.get('dashboard/layout', { params: { visao: visaoDesejada } });
            setPodeGestao(!!data.gestao);
            setVisao(data.visao);
            // layout null = nunca personalizou; [] = o usuário removeu tudo de propósito
            setLayout(Array.isArray(data.layout) ? sanearLayout(data.layout, data.visao) : layoutPadrao(data.visao));
        } catch (e) {
            toast.error('Não foi possível carregar a Home.');
            setVisao('assessor');
            setLayout(layoutPadrao('assessor'));
        }
    };

    useEffect(() => {
        carregarLayout('gestao'); // o servidor devolve "assessor" se o usuário não tiver a permissão KPI
    }, []);

    const trocarVisao = (nova) => {
        if (nova === visao || editando) return;
        setFiltros((f) => ({ ...f, assessor_id: '' }));
        setResumo(null);
        setListas({});
        carregarLayout(nova);
    };

    /* Opções dos filtros ------------------------------------------------- */
    useEffect(() => {
        if (!visao) return;
        const params = visao === 'assessor' ? { assessor_id: localStorage.getItem('id_assessor') } : {};
        api.get('customers/select/get', { params })
            .then(({ data }) => setClientes((data || []).slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))))
            .catch(() => setClientes([]));
        if (visao === 'gestao') {
            api.get('assessor/get/select')
                .then(({ data }) => setAssessores(data || []))
                .catch(() => setAssessores([]));
        }
    }, [visao]);

    /* Dados -------------------------------------------------------------- */
    const fontes = useMemo(() => {
        const s = new Set();
        layout.forEach((m) => MODULOS[m.id].dados && s.add(MODULOS[m.id].dados));
        return Array.from(s).sort();
    }, [layout]);

    useEffect(() => {
        if (!visao) return;
        const datas = intervalo(filtros.periodo, filtros.de, filtros.ate);
        if (!datas) return; // período personalizado incompleto

        const params = { ...datas };
        if (filtros.cliente_id) params.cliente_id = filtros.cliente_id;
        if (visao === 'gestao' && filtros.assessor_id) params.assessor_id = filtros.assessor_id;

        const atual = ++requisicao.current;
        setCarregando(true);

        const chamadas = fontes.map((fonte) =>
            api.get(`dashboard/${fonte}`, { params })
                .then(({ data }) => ({ fonte, data }))
                .catch(() => ({ fonte, erro: true }))
        );

        Promise.all(chamadas).then((resultados) => {
            if (atual !== requisicao.current) return; // resposta de filtro antigo
            const novasListas = {};
            const novosErros = {};
            resultados.forEach((r) => {
                if (r.erro) novosErros[r.fonte] = true;
                else if (r.fonte === 'resumo') setResumo(r.data);
                else if (LISTAS.includes(r.fonte)) novasListas[r.fonte] = r.data;
            });
            setListas((l) => ({ ...l, ...novasListas }));
            setErros(novosErros);
            setCarregando(false);
        });
    }, [visao, filtros, fontes]);

    /* Edição ------------------------------------------------------------- */
    const acoes = {
        mover: (i, dir) => setLayout((l) => {
            const n = l.slice();
            const j = i + dir;
            if (j < 0 || j >= n.length) return l;
            [n[i], n[j]] = [n[j], n[i]];
            return n;
        }),
        largura: (i) => setLayout((l) => l.map((m, k) => (k === i ? { ...m, span: LARGURAS[(LARGURAS.indexOf(m.span) + 1) % LARGURAS.length] } : m))),
        remover: (i) => setLayout((l) => l.filter((_, k) => k !== i)),
        adicionar: (id) => setLayout((l) => l.concat({ id, span: MODULOS[id].tipo === 'kpi' ? 3 : 6 })),
    };

    const iniciarEdicao = () => {
        layoutSalvo.current = layout;
        setEditando(true);
    };

    const cancelarEdicao = () => {
        setLayout(layoutSalvo.current || layout);
        setEditando(false);
    };

    const salvarLayout = async (novo) => {
        setSalvando(true);
        try {
            await api.put('dashboard/layout', { visao, layout: novo });
            if (novo === null) setLayout(layoutPadrao(visao));
            setEditando(false);
            toast.success(novo === null ? 'Layout padrão restaurado.' : 'Layout salvo.');
        } catch (e) {
            toast.error('Não foi possível salvar o layout.');
        } finally {
            setSalvando(false);
        }
    };

    const naTela = new Set(layout.map((m) => m.id));
    const disponiveis = visao ? Object.keys(MODULOS).filter((id) => !naTela.has(id) && MODULOS[id].visoes.includes(visao)) : [];

    if (!visao) return <LinearProgress />;

    return (
        <ThemeProvider theme={tema}>
        <div className={classes.pagina}>
            <header className={classes.cabecalho}>
                <div>
                    <div className={classes.subtitulo}>{dayjs().locale('pt-br').format('dddd, D [de] MMMM')}</div>
                    <h1 className={classes.titulo}>Bem-vindo{nome ? `, ${nome.split(' ')[0]}` : ''}</h1>
                </div>
                <div className={classes.acoesCabecalho}>
                    {podeGestao && (
                        <ButtonGroup size="small" disabled={editando} aria-label="Visão">
                            <Button variant={visao === 'gestao' ? 'contained' : 'outlined'} color={visao === 'gestao' ? 'primary' : 'default'} onClick={() => trocarVisao('gestao')}>Gestão</Button>
                            <Button variant={visao === 'assessor' ? 'contained' : 'outlined'} color={visao === 'assessor' ? 'primary' : 'default'} onClick={() => trocarVisao('assessor')}>Assessor</Button>
                        </ButtonGroup>
                    )}
                    {editando ? (
                        <>
                            <Button size="small" onClick={cancelarEdicao} disabled={salvando}>Cancelar</Button>
                            <Button size="small" variant="contained" color="primary" onClick={() => salvarLayout(layout)} disabled={salvando}>Salvar layout</Button>
                        </>
                    ) : (
                        <Button size="small" variant="outlined" startIcon={<DashboardOutlinedIcon />} onClick={iniciarEdicao}>Personalizar</Button>
                    )}
                </div>
            </header>

            <FiltrosHome
                filtros={filtros}
                onChange={setFiltros}
                clientes={clientes}
                assessores={assessores}
                mostrarAssessor={visao === 'gestao'}
            />

            {editando && (
                <section className={classes.catalogo} aria-label="Catálogo de módulos">
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <b style={{ color: '#1F4615', fontSize: 14 }}>Reorganize, redimensione ou remova módulos. Adicione os que estiverem fora da tela:</b>
                        <Button size="small" onClick={() => salvarLayout(null)} disabled={salvando}>Restaurar padrão</Button>
                    </div>
                    <div className={classes.catalogoItens}>
                        {disponiveis.length === 0 && <span style={{ fontSize: 13 }}>Todos os módulos já estão na tela.</span>}
                        {disponiveis.map((id) => (
                            <Button key={id} size="small" variant="outlined" style={{ background: '#fff' }} onClick={() => acoes.adicionar(id)}>
                                + {MODULOS[id].titulo}
                            </Button>
                        ))}
                    </div>
                </section>
            )}

            {/* barra de carregamento sem ocupar espaço no layout */}
            <div style={{ height: 4, margin: '-12px 0 -16px' }}>{carregando && <LinearProgress />}</div>

            <div className={classes.grade}>
                {layout.map((m, i) => (
                    <Modulo
                        key={m.id}
                        id={m.id}
                        span={m.span}
                        indice={i}
                        total={layout.length}
                        editando={editando}
                        resumo={resumo}
                        listas={listas}
                        erros={erros}
                        visao={visao}
                        acoes={acoes}
                    />
                ))}
            </div>

            {layout.length === 0 && (
                <div className={classes.vazio} style={{ textAlign: 'center', padding: 40 }}>
                    Nenhum módulo na tela. Use “Personalizar” para adicionar.
                </div>
            )}
        </div>
        </ThemeProvider>
    );
};

export default Home;
